import {
  ClipboardList,
  TestTube2,
  Play,
  Code2,
  Monitor,
  FileEdit,
} from 'lucide-react';

export const workflowSteps = [
  {
    step: 1,
    title: 'Generate Task Stories',
    icon: ClipboardList,
    color: '#6366F1',
    tags: [
      { label: 'Task Management', type: 'default' },
      { label: 'Performance Detection', type: 'performance' },
      { label: 'User Approval', type: 'default' },
      { label: 'ADO Integration', type: 'default' },
    ],
    description: [
      'Retrieves user story details from Azure DevOps work items including requirements and acceptance criteria',
      'Analyzes content for performance testing needs by scanning for performance-related keywords (load, stress, scalability, response time, etc.)',
      'Generates task list dynamically based on whether performance testing is required or not',
      'Seeks user approval before creating any tasks in Azure DevOps',
      'Links all tasks to the parent user story upon creation',
    ],
    inputs: [
      { name: 'user_story', desc: 'Work item ID (e.g., 12345)' },
      { name: 'project', desc: 'Project name (defaults to "Pilot-GenAIImplementation")' },
    ],
    outputs: [
      'Without Performance Testing: Functional Test Design, Automated Test Development, Functional Test Execution tasks',
      'With Performance Testing: All above plus Performance Test Scripting and Performance Test Execution tasks',
      'All tasks linked to the parent user story in Azure DevOps',
      'Confirmation of task IDs created',
    ],
  },
  {
    step: 2,
    title: 'Generate Functional Test Scenarios',
    icon: TestTube2,
    color: '#0097A9',
    tags: [
      { label: 'BDD Format', type: 'testing' },
      { label: 'Test Design', type: 'default' },
      { label: 'User Approval', type: 'default' },
      { label: 'ADO Integration', type: 'default' },
    ],
    description: [
      'Extracts requirements and acceptance criteria from the specified user story work item',
      'Analyzes parent feature to gather context and similar requirements',
      'Generates comprehensive test scenarios in BDD format covering positive, negative, and edge cases',
      'Presents test scenarios to user for approval before creating test cases in Azure DevOps',
      'Creates Test Case work items in Azure DevOps with custom fields tracking auto-generation',
      'Links test cases to the parent user story for traceability',
    ],
    inputs: [
      { name: 'user_story', desc: 'Work item ID (e.g., 12345)' },
      { name: 'project', desc: 'Project name (defaults to "QualityEngineeringGIT")' },
    ],
    outputs: [
      'Functional test scenarios in BDD (Given-When-Then) format',
      'Test cases covering happy path, negative, and edge case scenarios',
      'Azure DevOps Test Case work items with auto-generation tracking fields',
      'Test case IDs for reference',
    ],
  },
  {
    step: 3,
    title: 'Execute Functional Tests with Playwright MCP',
    icon: Play,
    color: '#DC2626',
    tags: [
      { label: 'Test Execution', type: 'execution' },
      { label: 'Playwright MCP', type: 'automation' },
      { label: 'Defect Creation', type: 'default' },
      { label: 'Real-time', type: 'execution' },
    ],
    description: [
      'Retrieves test case details from Azure DevOps including navigation steps and verification points',
      'Translates manual test steps into Playwright commands dynamically without writing automation scripts',
      'Executes tests using Playwright MCP following the exact steps specified in the test case',
      'Handles user authentication by pausing for manual credential entry when needed',
      'Creates defects automatically when test failures occur (with user approval)',
      'Generates execution report documenting pass/fail status for each step',
    ],
    inputs: [
      { name: 'test_case', desc: 'Test case work item ID from Azure DevOps' },
      { name: 'project', desc: 'Project name' },
    ],
    outputs: [
      'Live test execution using Playwright MCP',
      'Step-by-step execution status with pass/fail for each verification point',
      'Error details for failed steps with overall test summary',
      'Bug/Defect work items in Azure DevOps when failures occur (linked to test case)',
    ],
  },
  {
    step: 4,
    title: 'Generate Playwright Automated Test Scenarios',
    icon: Code2,
    color: '#059669',
    tags: [
      { label: 'Playwright', type: 'automation' },
      { label: 'BDD Format', type: 'testing' },
      { label: 'Page Objects', type: 'default' },
      { label: 'TypeScript', type: 'default' },
    ],
    description: [
      'Analyzes user story requirements and acceptance criteria comprehensively',
      'Examines existing test coverage by searching current Playwright test files and page objects',
      'Generates complete Playwright test automation code including spec files, Page Object Model classes, and fixtures',
      'Follows BDD format and best practices with Given-When-Then structure',
      'Presents proposed tests for approval before creating any files',
      'Provides multi-browser support (Chromium, Firefox, WebKit, Mobile)',
    ],
    inputs: [
      { name: 'user_story', desc: 'Work item ID' },
      { name: 'project', desc: 'Project name' },
    ],
    outputs: [
      'Test spec files (*.spec.ts) with BDD-formatted scenarios',
      'Page Object Model classes with reusable page interactions',
      'Test fixtures with data files and setup/teardown configurations',
      'Test coverage report mapping scenarios to acceptance criteria',
    ],
  },
  {
    step: 5,
    title: 'Generate Automated Tests from ADO Test Cases',
    icon: Monitor,
    color: '#D97706',
    tags: [
      { label: 'SpecFlow/ReqnRoll', type: 'automation' },
      { label: 'BDD', type: 'testing' },
      { label: 'Selenium', type: 'default' },
      { label: 'Appium', type: 'default' },
      { label: 'C#', type: 'default' },
    ],
    description: [
      'Retrieves test case details from Azure DevOps work items',
      'Analyzes existing automation coverage in the C# test automation framework',
      'Generates BDD feature files using SpecFlow/ReqnRoll for UI testing',
      'Implements step definitions for test execution with proper UI interactions',
      'Creates/updates Page Object Model classes using Selenium WebDriver or Appium',
      'Supports multiple platforms: Web (Selenium), iOS/Android (Appium)',
    ],
    inputs: [
      { name: 'test_case', desc: 'Azure DevOps test case work item ID' },
      { name: 'project', desc: 'Project name' },
    ],
    outputs: [
      'BDD Feature Files with scenario outlines, background steps, and tags (@ui, @smoke, @regression)',
      'C# Step Definitions with FluentAssertions and async/await patterns',
      'Page Object classes with cross-platform element locators',
      'Configuration updates (appsettings.json, driver configs, test data)',
    ],
  },
  {
    step: 6,
    title: 'Update User Story from Wiki',
    icon: FileEdit,
    color: '#7C3AED',
    tags: [
      { label: 'User Story Enhancement', type: 'default' },
      { label: 'Wiki Integration', type: 'default' },
      { label: 'Role-Based', type: 'default' },
      { label: 'User Approval', type: 'default' },
    ],
    description: [
      'Retrieves and displays current user story details for review',
      'Analyzes existing attachments and can enhance user story based on attached documents',
      'Asks for user preferences before making any changes (user-driven approach)',
      'Supports role-based perspectives: Quality Engineer (testability, quality gates) and Product Owner (business value, user needs)',
      'Updates user story only with approved enhancements',
      'Adds comments documenting all changes for audit trail',
    ],
    inputs: [
      { name: 'user_story', desc: 'Work item ID' },
      { name: 'project', desc: 'Project name' },
      { name: 'user_role', desc: 'QualityEngineer or ProductOwner' },
      { name: 'wiki_identifier', desc: '(Optional) Wiki page path or wiki name' },
    ],
    outputs: [
      'Enhanced user story with attachment-based or wiki-based improvements',
      'Before/after comparison showing changes',
      'Role-specific enhancements (QE or PO perspective)',
      'Audit trail with work item comments documenting all changes',
    ],
  },
];

export const workflowSummary = [
  'Analyze user story and create tasks (functional, automated, performance testing)',
  'Generate functional test scenarios in BDD format and create test cases in Azure DevOps',
  'Execute functional tests using Playwright MCP with real-time translation of test steps',
  'Generate complete Playwright automation code (specs, page objects, fixtures)',
  'Generate C# UI automation using SpecFlow/ReqnRoll for mobile/web testing',
  'Enhance user stories with wiki content and attachments based on user role',
];

export const pipelineStages = [
  'User Story',
  'Task Creation',
  'Test Design',
  'Test Generation',
  'Test Automation',
  'Test Execution',
  'Wiki Updates',
];
